import { useEffect, useState, useMemo, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { XCircle, TrendingUp, TrendingDown, Pencil, AlertTriangle, ChevronRight, ChevronDown, Users as UsersIcon, ShieldCheck, Plus, X, ArrowUp, ArrowDown, ArrowUpDown, Search } from "lucide-react";
import api, { formatDetail } from "@/lib/api";
import AuditLogButton from "@/components/AuditLogButton";
import { useAuth } from "@/lib/auth";
import { useAccess } from "@/lib/access";
import { useRefresh } from "@/lib/useRefresh";
import { Button } from "@/components/ui/button";
import ActiveProjectDialog from "@/components/ActiveProjectDialog";
import EditProjectDialog from "@/components/EditProjectDialog";
import ProfitStatusBadge from "@/components/cost-control/ProfitStatusBadge";
import ProfitDrilldownDialog from "@/components/cost-control/ProfitDrilldownDialog";
import DeleteProjectDialog from "@/components/DeleteProjectDialog";
import CloseProjectDialog from "@/components/CloseProjectDialog";
import IncomeLedgerDialog from "@/components/IncomeLedgerDialog";
import ExpenseLedgerDialog from "@/components/ExpenseLedgerDialog";
// 149-cw-r120 (2026-08-29): AccurateUpdateMenu tidak lagi diimport — semua tombol
// input keuangan dihapus. Laporan Keuangan menjadi satu-satunya antarmuka finance.
import RetentionClaimsDialog from "@/components/RetentionClaimsDialog";
import BulkChangeParentDialog from "@/components/BulkChangeParentDialog";
// 2026-06-11 (TENDER-SUNSET): TenderPipelinePage dihapus permanen.
// Tab tender di ProjectsPage juga dihapus di edit terpisah di bawah.
import ProjectLifecycleDialog from "@/components/ProjectLifecycleDialog";
import SummaryProjectView from "@/components/SummaryProjectView";
// 2026-06-15: Fase 1 rework — Summary Project full-width + tombol "Monitoring Project"
// per baris. Panel detail kanan dihapus per spesifikasi user.
import SummaryProjectListView from "@/components/SummaryProjectListView";
import { fmtDate, formatIDR, PROJECT_STATUS, DIVISION_COLOR, deriveProjectStatus } from "@/lib/constants";

// 149-cw-r128 R5.1 (2026-08-30): Monitoring AR + Kalender AR DIHAPUS dari
// Project Management. Redirect otomatis ke Laporan Keuangan (invoice-piutang
// atau retensi-collection). Import ARMonitoringView/ARCalendarView dihapus.
import WOMonitoringView from "@/components/projects/WOMonitoringView";
import WOMasterDetailView from "@/components/projects/WOMasterDetailView";
import AccessListPanel from "@/components/projects/AccessListPanel";
import ProjectFilterPresetsBar from "@/components/projects/ProjectFilterPresetsBar";
import BudgetAlertDialog from "@/components/BudgetAlertDialog";
import { SortH, computeRetentionHeld, computeRetentionClaimed } from "@/components/projects/projectHelpers";
import { SisaWaktuBadge } from "@/components/projects/SisaWaktuBadge";
import { useSummaryFilteredProjects } from "@/hooks/useSummaryFilteredProjects";
import StickyContextBar from "@/components/StickyContextBar";


// 149-cq (PROMPT 16-R2A): Breakdown jumlah project berdasarkan tipe supaya user
// tidak tertukar antara "83 project" (total gabungan) dengan lifecycle Aktif yg
// hanya 32. Label eksplisit: Utama · Internal · Sub · VO · WO — TIDAK memakai
// kata "project" untuk campuran.
// 149-cr (PROMPT 16-R2A-R1): tambahkan slot `internal` supaya total breakdown
// = jumlah record di tabel (sebelumnya 30+0+10+41=81 padahal tabel 83 karena
// 2 internal). Internal project TIDAK dihitung sebagai project utama.
// 149-da-r2 (PROMPT 16-PROJECT-LOAD-R2): count breakdown honors load state.
// Selama loading render skeleton pills (—), NEVER menampilkan angka 0.
function ProjectCountBreakdown({ projects, loadState }) {
  if (loadState !== "success") {
    return (
      <span className="text-slate-400 normal-case" data-testid="project-count-breakdown-loading">
        <span className="inline-block bg-slate-200 animate-pulse w-6 h-3 align-middle" /> utama · <span className="inline-block bg-slate-200 animate-pulse w-4 h-3 align-middle" /> internal · <span className="inline-block bg-slate-200 animate-pulse w-4 h-3 align-middle" /> sub · <span className="inline-block bg-slate-200 animate-pulse w-4 h-3 align-middle" /> VO · <span className="inline-block bg-slate-200 animate-pulse w-4 h-3 align-middle" /> WO
      </span>
    );
  }
  const utama = projects.filter((p) => p.project_type === "new_project" && !p.parent_project_id).length;
  const internal = projects.filter((p) => p.project_type === "internal").length;
  const sub = projects.filter((p) => p.project_type === "new_project" && p.parent_project_id).length;
  const vo = projects.filter((p) => p.project_type === "variation_order").length;
  const wo = projects.filter((p) => p.project_type === "working_order").length;
  return (
    <span className="text-slate-400 normal-case" data-testid="project-count-breakdown">
      <span className="text-[#0B1D3A] font-semibold">{utama}</span> utama · {internal} internal · {sub} sub · {vo} VO · {wo} WO
    </span>
  );
}

export default function ProjectsPage() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const nav = useNavigate();
  const [projects, setProjects] = useState([]);
  // 149-cy → 149-da-r2 (PROMPT 16-PROJECT-LOAD-R2): eksplisit load state.
  // States: "idle" (menunggu auth) | "loading" (initial) | "success" | "empty"
  // (success + 0 rows) | "error" | "refetching" (preserve last-known-good).
  const [loadState, setLoadState] = useState("idle");
  const [loadError, setLoadError] = useState(null);
  const initialTab = ["summary", "wo"].includes(searchParams.get("tab")) ? searchParams.get("tab") : "summary";
  const [tab, setTab] = useState(initialTab);
  // 149-cw-r89: view state untuk WO Monitoring (lifted dari WOTabContainer supaya
  // toggle bisa di-render inline dgn tab bar).
  const [woView, setWoView] = useState(() => {
    try { return localStorage.getItem("ens_wo_view_mode_v1") || "master_detail"; } catch { return "master_detail"; }
  });
  // 2026-06-20 (code review): deep-link `?wo=<id>` hanya diproses view master_detail —
  // paksa mode tsb saat param wo ada, meski user menyimpan mode board.
  const effectiveWoView = searchParams.get("wo") ? "master_detail" : woView;
  useEffect(() => { try { localStorage.setItem("ens_wo_view_mode_v1", woView); } catch { /* ignore */ } }, [woView]);
  // Sync tab dari query string ketika navigation (mis. dari notif deep-link).
  useEffect(() => {
    const q = searchParams.get("tab");
    // R5.1 (r128): redirect tab lama ke Laporan Keuangan.
    if (q === "ar") { nav("/finance-report?tab=invoice-piutang", { replace: true }); return; }
    if (q === "calendar") { nav("/finance-report?tab=retensi-collection", { replace: true }); return; }
    if (q && ["summary", "wo"].includes(q)) setTab(q);
  }, [searchParams, nav]);
  // Filter untuk view Summary: "all" (default), "combined" (hanya proyek NP dengan
  // vo_aggregation="combined"), "retention_pending" (proyek yang punya entry retention
  // claim = menunggu pembayaran). User request 09-Feb-2026.
  const [summaryFilter, setSummaryFilter] = useState("all"); // 'summary' | 'ar' | 'wo'
  // Status filter — hide/show projects by lifecycle status. Default "all" shows every
  // project regardless of status. User request 2026-02-15: mampu lihat historical (Completed/
  // Cancelled) berdampingan dengan Active tanpa harus filter di kolom Status.
  const [statusFilter, setStatusFilter] = useState("all"); // 'all' | 'active' | 'completed' | 'cancelled'
  // Custom preset filter (2026-02-15) — user bisa bookmark selection of projects
  // via ProjectFilterPresetsBar. Ketika active, hanya project di preset yang tampil.
  const [activePresetId, setActivePresetId] = useState(null);
  const [presetProjectIds, setPresetProjectIds] = useState(null); // null = no preset
  // Search query — filter Summary Project rows berdasarkan nama project / client /
  // WO number / VO name / lokasi. Case-insensitive, tokenized (multi-kata AND).
  const [searchQ, setSearchQ] = useState("");
  const [summarySort, setSummarySort] = useState({ key: null, dir: "asc" });
  const [open, setOpen] = useState(false);
  const [createPreset, setCreatePreset] = useState(null); // {category, type} — preset dialog create (tab Monitoring WO)
  const [closeDlg, setCloseDlg] = useState({ open: false, project: null });
  const [incDlg, setIncDlg] = useState({ open: false, project: null });
  const [expDlg, setExpDlg] = useState({ open: false, project: null });
  const [retentionDlg, setRetentionDlg] = useState(false);
  const [editDlg, setEditDlg] = useState({ open: false, project: null });
  // PROMPT 16-DELETE-CONFIRM-DIALOG (2026-08-26): typed-name + reason gate.
  const [deleteDlg, setDeleteDlg] = useState({ open: false, project: null, isCompleted: false });
  const [expanded, setExpanded] = useState({});
  // 149-cw-r116 (2026-08-29): Budget alerts dialog — klik banner "Near/Over Budget"
  // supaya user bisa lihat daftar project mana yang melebihi/mendekati batas kontrak.
  const [budgetAlertDlg, setBudgetAlertDlg] = useState({ open: false, kind: null });
  const [childrenOpen, setChildrenOpen] = useState({});
  // 149-ak: state utk bulk pindah parent dari Monitoring WO
  const [bulkDlg, setBulkDlg] = useState({ open: false, projects: [], onClear: null });
  // 149-bh: state Project Lifecycle dialog (Aktif → Closing → Closed)
  const [lifecycleDlg, setLifecycleDlg] = useState({ open: false, project: null });

  // 149-cy → 149-da-r2: request scoping via ref + Promise.all untuk atomic
  // update kartu lifecycle. Pergantian tab/filter cepat atau refresh manual
  // TIDAK boleh menyebabkan respons lama menimpa data baru.
  const loadTokenRef = useRef(0);
  const hasLoadedOnceRef = useRef(false);
  const [ccSummaries, setCcSummaries] = useState({});  // R227: profit_view kanonis
  // R229: pisah open vs pid — isi dialog tidak berubah saat animasi tutup.
  const [profitDrillPid, setProfitDrillPid] = useState(null);
  const [profitDrillOpen, setProfitDrillOpen] = useState(false);
  const load = async () => {
    if (!user) return;  // spec: request menunggu autentikasi selesai
    const myToken = ++loadTokenRef.current;
    // First load → "loading" full skeleton. Refetch → "refetching" preserve data.
    setLoadState(hasLoadedOnceRef.current ? "refetching" : "loading");
    setLoadError(null);
    try {
      // 2026-06-11 (TENDER-SUNSET): tidak lagi memanggil /tenders.
      const projRes = await api.get("/active-projects");
      if (myToken !== loadTokenRef.current) return;  // stale — abaikan
      const rows = Array.isArray(projRes.data) ? projRes.data : [];
      setProjects(rows);
      setLoadState(rows.length === 0 ? "empty" : "success");
      hasLoadedOnceRef.current = true;
      // R227: profit view kanonis per proyek (engine) — kolom Profit memakai
      // angka yang sama dengan Monitoring & Cost Control. Gagal → fallback
      // tampilan lama (tidak memblokir daftar proyek).
      try {
        const sRes = await api.get("/cost-control/summaries");
        if (myToken === loadTokenRef.current) setCcSummaries(sRes.data?.summaries || {});
      } catch { /* fallback tampilan lama */ }
    } catch (e) {
      if (myToken !== loadTokenRef.current) return;
      setLoadState("error");
      setLoadError(formatDetail(e.response?.data?.detail) || "Gagal memuat data project.");
    }
  };
  useEffect(() => {
    if (!user) return;
    // Snapshot ref object supaya cleanup pakai referensi yang di-capture pada
    // saat effect mount (React exhaustive-deps warning re: ref.current di cleanup).
    // Object identity `loadTokenRef` stabil — cukup snapshot tanpa .current.
    const tokenRef = loadTokenRef;
    load();
    // Cleanup: bump token supaya in-flight request tidak menulis state
    // setelah unmount atau user berganti.
    return () => { tokenRef.current++; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);
  useRefresh(load);

  // Access matrix — new 5-perm system (2026-02-11). Old sub-features consolidated
  // into `projects` feature with granular Create/Update/Delete/View permissions.
  // BOD Lv1 super-admin bypass is handled inside canPerm.
  const { canPerm } = useAccess();
  const canCreate = canPerm(user, "projects", "create");
  const canEditActive = canPerm(user, "projects", "update");
  const canEditCompleted = canPerm(user, "projects", "delete");
  // Delete completed project — same elevated matrix perm as edit-completed (projects.delete).
  const canDeleteCompleted = canPerm(user, "projects", "delete");
  const canClose = canPerm(user, "projects", "close");
  const canCancelActive = canPerm(user, "projects", "delete");
  const canViewLedger = canPerm(user, "projects", "view");
  const canLedger = canPerm(user, "projects", "update");

  // Empty-state helper: user is subject to per-user project visibility gating
  // (i.e., NOT BOD Lv1 and doesn't have "all" scope on projects) AND the backend
  // returned zero non-internal projects. In this case, either they have not been
  // assigned any client project, or those assignments are still empty.
  const { visibilityScope } = useAccess();
  const projectsScope = visibilityScope(user, "projects");
  const isVisibilityGated = projectsScope !== "all";
  const nonInternalCount = projects.filter((p) => p.project_type !== "internal").length;
  const showAssignmentEmpty = isVisibilityGated && projects.length > 0 && nonInternalCount === 0;
  const showFullyEmpty = isVisibilityGated && projects.length === 0;
  // When the current user is visibility-gated AND has zero non-internal projects assigned,
  // hide the internal (Head Office / Overhead) rows from the table too — they should only
  // see the empty-state banner. Non-gated users (BOD / FAT Lv2) always see everything.
  const displayedProjectsBase = (isVisibilityGated && nonInternalCount === 0)
    ? projects.filter((p) => p.project_type !== "internal")
    : projects;

  // Apply sort — only affects TOP-LEVEL (non-child) rows. Children (VO/WO) remain in-place
  // under their parent via childrenByParent map.
  const displayedProjects = useMemo(() => {
    if (!summarySort.key) return displayedProjectsBase;
    const dir = summarySort.dir === "asc" ? 1 : -1;
    const key = summarySort.key;
    const rank = (p) => {
      switch (key) {
        case "type": return p.project_type === "internal" ? 0 : (p.project_type === "variation_order" ? 2 : p.project_type === "working_order" ? 3 : 1);
        case "name": return (p.project_name || "").toLowerCase();
        case "client": return (p.client || "").toLowerCase();
        case "contract": return Number(p.contract_value || 0);
        case "profit": {
          const inc = p.total_income || 0; const exp = p.total_expense || 0; return inc - exp;
        }
        case "days": {
          // Sumber kanonis backend (Asia/Jakarta) — frontend tidak menghitung ulang.
          return p.schedule?.elapsed_days ?? -1;
        }
        case "status": return p.status || "";
        default: return 0;
      }
    };
    const parents = displayedProjectsBase.filter((p) => p.project_type !== "variation_order" && p.project_type !== "working_order");
    const children = displayedProjectsBase.filter((p) => p.project_type === "variation_order" || p.project_type === "working_order");
    parents.sort((a, b) => {
      const ra = rank(a); const rb = rank(b);
      if (ra < rb) return -1 * dir;
      if (ra > rb) return 1 * dir;
      return 0;
    });
    // Interleave children (VO/WO) immediately after their parent so hierarchy tetap
    // terjaga saat sort — user request 09-Feb-2026: "saat sort project, VO ataupun WO
    // tetap menempel ke parent project". Children yatim (parent tidak ada di list) ditaruh
    // di akhir dengan urutan aslinya.
    const childrenByParent = new Map();
    const orphans = [];
    for (const c of children) {
      const pid = c.parent_project_id;
      if (pid && parents.some((p) => p.id === pid)) {
        if (!childrenByParent.has(pid)) childrenByParent.set(pid, []);
        childrenByParent.get(pid).push(c);
      } else {
        orphans.push(c);
      }
    }
    const out = [];
    for (const parent of parents) {
      out.push(parent);
      const kids = childrenByParent.get(parent.id);
      if (kids && kids.length) out.push(...kids);
    }
    out.push(...orphans);
    return out;
  }, [displayedProjectsBase, summarySort]);

  // Apply summary filter + hierarki injection via extracted hook (149-al refactor).
  // Hook mengembalikan { filteredProjects, filteredIds } dan side-effect auto-expand
  // childrenOpen saat filter aktif. Semua kontrak 149-ai (force-inject parent, walk-up
  // chain, orphan handling) dipertahankan dalam hook.
  const { filteredProjects, filteredIds } = useSummaryFilteredProjects({
    displayedProjects,
    summaryFilter,
    statusFilter,
    searchQ,
    presetProjectIds,
    setChildrenOpen,
  });

  const cancel = async (id, isCompleted) => {
    // PROMPT 16-DELETE-CONFIRM-DIALOG: buka dialog dgn typed-name + reason gate
    // menggantikan `window.confirm` bare — reason WAJIB & selalu tersimpan audit log.
    const proj = projects.find((p) => p.id === id) || null;
    if (!proj) return;
    setDeleteDlg({ open: true, project: proj, isCompleted: !!isCompleted });
  };

  const doDeleteProject = async (reason) => {
    const proj = deleteDlg.project;
    if (!proj) return;
    try {
      await api.delete(`/active-projects/${proj.id}`, { params: { reason } });
      toast.success(deleteDlg.isCompleted ? "Project deleted" : "Project cancelled");
      setDeleteDlg({ open: false, project: null, isCompleted: false });
      load();
    } catch (err) { toast.error(formatDetail(err.response?.data?.detail)); }
  };

  const reopen = async (id) => {
    if (!window.confirm("Buka ulang project yang sudah completed ini?")) return;
    try {
      await api.patch(`/active-projects/${id}/reopen`);
      toast.success("Project dibuka kembali");
      load();
    } catch (err) { toast.error(formatDetail(err.response?.data?.detail)); }
  };

  const toggleExpand = (id) => setExpanded((e) => ({ ...e, [id]: !e[id] }));
  const toggleChildren = (id) => setChildrenOpen((e) => ({ ...e, [id]: !e[id] }));

  // Map of parent id → child projects (VO + WO)
  const childrenByParent = projects.reduce((acc, p) => {
    if (p.project_type === "variation_order" || p.project_type === "working_order") {
      const pid = p.parent_project_id;
      if (pid) (acc[pid] = acc[pid] || []).push(p);
    }
    return acc;
  }, {});

  const overBudgetCount = projects.filter(
    (p) => p.project_type !== "internal" && (p.contract_value || 0) > 0 && (p.total_expense || 0) > (p.contract_value || 0)
  ).length;
  const nearBudgetCount = projects.filter((p) => {
    if (p.project_type === "internal") return false;
    const cv = p.contract_value || 0;
    const exp = p.total_expense || 0;
    if (cv <= 0) return false;
    const pct = (exp / cv) * 100;
    return pct >= 80 && exp <= cv;
  }).length;

  return (
    <div className="p-6 space-y-4">
      {/* 149-cw-r102 (2026-08-28): Title block muncul di SEMUA tab (tender,
          summary, ar, wo, calendar) — bukan hanya WO. Label breadcrumb +
          heading H1 di-derive dari state `tab`. Kanan: budget alerts + aksi. */}
      <div className="flex items-start justify-between gap-3 flex-wrap" data-testid="wo-title-alerts-row">
        {(() => {
          const TAB_TITLES = {
            summary:  "Ringkasan Proyek",
            ar:       "Pemantauan Piutang (AR)",
            wo:       "Pemantauan WO",
            calendar: "Kalender Piutang (AR)",
          };
          const title = TAB_TITLES[tab] || "Manajemen Proyek";
          return (
            <div className="min-w-0" data-testid="wo-title-block">
              <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 flex items-center gap-1.5">
                <span>Manajemen Proyek</span>
                <span className="text-slate-400">›</span>
                <span className="text-slate-700">{title}</span>
              </div>
              <h1 className="text-2xl sm:text-3xl font-bold text-[#0B1D3A] tracking-tight mt-1">{title}</h1>
            </div>
          );
        })()}
        <div className="flex flex-wrap gap-3 justify-end items-center">
          {(overBudgetCount > 0 || nearBudgetCount > 0) && tab !== "summary" && (
            <div className="flex flex-wrap gap-3 justify-end" data-testid="budget-alerts">
              {overBudgetCount > 0 && (
                <button
                  type="button"
                  data-testid="budget-alert-over"
                  onClick={() => setBudgetAlertDlg({ open: true, kind: "over" })}
                  title="Klik untuk lihat daftar project yang over budget"
                  className="flex items-center gap-2 bg-rose-50 border border-rose-300 border-l-4 border-l-rose-500 px-4 py-2.5 hover:bg-rose-100 hover:border-l-rose-600 transition-colors cursor-pointer text-left focus:outline-none focus:ring-2 focus:ring-rose-500 focus:ring-offset-1"
                >
                  <AlertTriangle size={16} className="text-rose-600" />
                  <div className="text-xs">
                    <div className="overline text-rose-700 flex items-center gap-1">Over Budget <span className="text-[9px] opacity-70">▸ lihat daftar</span></div>
                    <div className="font-semibold text-rose-800 tabular-nums">
                      {overBudgetCount} project{overBudgetCount > 1 ? "s" : ""} exceed contract value
                    </div>
                  </div>
                </button>
              )}
              {nearBudgetCount > 0 && (
                <button
                  type="button"
                  data-testid="budget-alert-near"
                  onClick={() => setBudgetAlertDlg({ open: true, kind: "near" })}
                  title="Klik untuk lihat daftar project yang mendekati batas kontrak"
                  className="flex items-center gap-2 bg-amber-50 border border-amber-300 border-l-4 border-l-amber-500 px-4 py-2.5 hover:bg-amber-100 hover:border-l-amber-600 transition-colors cursor-pointer text-left focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-1"
                >
                  <AlertTriangle size={16} className="text-amber-600" />
                  <div className="text-xs">
                    <div className="overline text-amber-700 flex items-center gap-1">Near Budget Limit <span className="text-[9px] opacity-70">▸ lihat daftar</span></div>
                    <div className="font-semibold text-amber-800 tabular-nums">
                      {nearBudgetCount} project{nearBudgetCount > 1 ? "s" : ""} used ≥ 80% of contract
                    </div>
                  </div>
                </button>
              )}
            </div>
          )}
          {/* 2026-09-29 (user request): "+ New Project" tidak diperlukan di tab
              Monitoring WO — penambahan dilakukan dari Summary Project. */}
          {canCreate && tab !== "wo" && (
            <Button data-testid="btn-new-project" onClick={() => nav("/projects/new")} className="rounded-none bg-[#FF5A1F] hover:bg-[#e04a13] uppercase text-xs tracking-wider font-semibold">+ Proyek Baru</Button>
          )}
        </div>
      </div>

      {/* 149-da-r2: empty-state banner HANYA muncul setelah loadState=success/empty.
          Selama loading, jangan render banner ini — spec: "jangan menampilkan
          empty state sebelum API berhasil mengembalikan data kosong". */}
      {(loadState === "success" || loadState === "empty") && (showAssignmentEmpty || showFullyEmpty) && (
        <div className="bg-amber-50 border-l-4 border-amber-500 border border-amber-200 p-4 flex items-center gap-3" data-testid="projects-no-assignment">
          <AlertTriangle size={20} className="text-amber-600 shrink-0" />
          <div className="overline text-amber-800 font-semibold text-sm">Belum Ada Project yang Di-Assign</div>
        </div>
      )}

      {/* Tab Navigation: Tender / Summary / AR / WO — 149-cw-r89: flex row
          justify-between supaya area kanan bisa dipakai untuk toggle TAMPILAN +
          judul kompak (khusus tab WO). */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="inline-flex border border-slate-300" data-testid="proj-tab-nav">
          <button type="button" data-testid="tab-summary" onClick={() => setTab("summary")}
            className={`px-4 py-2 text-xs uppercase tracking-wider font-semibold ${tab === "summary" ? "bg-[#0B1D3A] text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>
            Ringkasan Proyek        </button>
          <button type="button" data-testid="tab-wo" onClick={() => setTab("wo")}
            className={`px-4 py-2 text-xs uppercase tracking-wider font-semibold border-l border-slate-300 ${tab === "wo" ? "bg-sky-700 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>
            Pemantauan WO
          </button>
        </div>
        {tab === "wo" && (
          <div className="flex items-center gap-2 flex-wrap justify-end" data-testid="wo-tab-right-slot">
            {/* 2026-09-29 (user request): pulihkan tombol tambah WO / Outlet di
                tab Monitoring WO — membuka dialog create langsung mode Maintenance. */}
            {canCreate && (
              <div className="flex items-center gap-2" data-testid="wo-create-buttons">
                <Button data-testid="btn-new-working-order"
                  onClick={() => { setCreatePreset({ category: "maintenance", type: "working_order" }); setOpen(true); }}
                  className="rounded-none bg-[#FF5A1F] hover:bg-[#e04a13] uppercase text-xs tracking-wider font-semibold">
                  + WO Baru
                </Button>
                <Button data-testid="btn-new-outlet" variant="outline"
                  onClick={() => { setCreatePreset({ category: "maintenance", type: "new_outlet" }); setOpen(true); }}
                  className="rounded-none uppercase text-xs tracking-wider font-semibold">
                  + Outlet Baru
                </Button>
              </div>
            )}
            <div className="flex items-center gap-2" data-testid="wo-view-toggle">
              <span className="overline text-slate-500 text-[10px]">Tampilan:</span>
              <div className="inline-flex border border-slate-300">
                <button type="button" data-testid="wo-view-master-detail" onClick={() => setWoView("master_detail")}
                  className={`px-3 py-1.5 text-[11px] uppercase tracking-wider font-semibold ${woView === "master_detail" ? "bg-[#0B1D3A] text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>
                  Master–Detail
                </button>
                <button type="button" data-testid="wo-view-board" onClick={() => setWoView("board")}
                  className={`px-3 py-1.5 text-[11px] uppercase tracking-wider font-semibold border-l border-slate-300 ${woView === "board" ? "bg-[#0B1D3A] text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>
                  Board
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <StickyContextBar
        segments={[
          "Manajemen Proyek",
          tab === "summary" ? "Ringkasan Proyek" :
          tab === "wo" ? "Pemantauan WO" : "",
        ]}
        extra={<ProjectCountBreakdown projects={projects} loadState={loadState} />}
        className={tab === "wo" ? "-mb-8" : ""}
      />

      {tab === "wo" && (
        <WOTabContainer
          projects={projects}
          view={effectiveWoView}
          onOpenEdit={(p) => setEditDlg({ open: true, project: p })}
          onBulkChangeParent={(chosen, onClear) => setBulkDlg({ open: true, projects: chosen, onClear })}
          onChanged={load}
          onDelete={(p) => cancel(p.id, p.status === "completed")}
        />
      )}

      {/* 149-bl: Redesigned master-detail Summary Project view. Legacy table dinonaktifkan
          via `false &&` di bawah supaya kode filter/kalkulasi lama tetap tersimpan sebagai
          referensi tanpa memicu render duplikat. */}
      {tab === "summary" && (
        <SummaryProjectListView
          projects={displayedProjectsBase}
          loadState={loadState}
          loadError={loadError}
          onRetry={load}
          canCreate={canCreate}
          onOpenNewProject={() => setOpen(true)}
        />
      )}
      {false && tab === "summary" && (
        <SummaryProjectView
          displayedProjectsBase={displayedProjectsBase}
          filteredProjects={filteredProjects}
          childrenByParent={childrenByParent}
          load={load}
          loadState={loadState}
          loadError={loadError}
          onRetry={load}
          onEdit={(p) => setEditDlg({ open: true, project: p })}
          onClose={(p) => setCloseDlg({ open: true, project: p })}
          onLifecycle={(p) => setLifecycleDlg({ open: true, project: p })}
          onReopen={(p) => reopen(p.id)}
          onCancel={(p) => cancel(p.id, p.status === "completed")}
          canEditActive={canEditActive}
          canEditCompleted={canEditCompleted}
          canClose={canClose}
          canCancelActive={canCancelActive}
          canDeleteCompleted={canDeleteCompleted}
          canLedger={canLedger}
          onOpenNewProject={() => setOpen(true)}
          budgetAlerts={(overBudgetCount > 0 || nearBudgetCount > 0) ? (
            <div className="flex flex-wrap gap-2 text-[11px]" data-testid="budget-alerts-compact">
              {overBudgetCount > 0 && (
                <button type="button" data-testid="budget-alert-compact-over"
                  onClick={() => setBudgetAlertDlg({ open: true, kind: "over" })}
                  title="Klik untuk lihat daftar project over budget"
                  className="inline-flex items-center gap-1 px-2 py-1 bg-rose-50 border border-rose-300 text-rose-700 hover:bg-rose-100 cursor-pointer">
                  <AlertTriangle size={12} /> {overBudgetCount} Over Budget
                </button>
              )}
              {nearBudgetCount > 0 && (
                <button type="button" data-testid="budget-alert-compact-near"
                  onClick={() => setBudgetAlertDlg({ open: true, kind: "near" })}
                  title="Klik untuk lihat daftar project mendekati batas kontrak"
                  className="inline-flex items-center gap-1 px-2 py-1 bg-amber-50 border border-amber-300 text-amber-800 hover:bg-amber-100 cursor-pointer">
                  <AlertTriangle size={12} /> {nearBudgetCount} Near Limit
                </button>
              )}
            </div>
          ) : null}
          advancedFilterBar={
            <div className="flex flex-wrap items-center gap-2 text-[11px]">
              {[
                { v: "all", label: "Semua" },
                { v: "combined", label: "VO Digabung" },
                { v: "retention_pending", label: "Klaim Retensi Aktif" },
                { v: "over_contract", label: "Melebihi Kontrak" },
              ].map((f) => (
                <button key={f.v} type="button" data-testid={`legacy-filter-${f.v}`}
                  onClick={() => setSummaryFilter(f.v)}
                  className={`px-3 py-1 uppercase tracking-wider font-semibold border ${summaryFilter === f.v ? "bg-[#0B1D3A] text-white border-[#0B1D3A]" : "bg-white text-slate-600 border-slate-300 hover:border-slate-500"}`}>
                  {f.label}
                </button>
              ))}
              <ProjectFilterPresetsBar />
            </div>
          }
        />
      )}

      {false && tab === "summary" && (
      <>
      {/* Filter chips — narrow the Summary view to only "combined-mode" parents,
          or "retention-claim pending" projects. User request 09-Feb-2026. */}
      <div className="flex flex-wrap items-center gap-2" data-testid="summary-filter-bar">
        <span className="overline text-slate-500 text-[10px]">Filter</span>
        {[
          { v: "all", label: "Semua", count: displayedProjectsBase.filter((p) => !["variation_order","working_order"].includes(p.project_type)).length },
          { v: "combined", label: "VO Digabung", count: displayedProjectsBase.filter((p) => p.project_type === "new_project" && p.vo_aggregation === "combined").length },
          { v: "retention_pending", label: "Klaim Retensi Aktif", count: displayedProjectsBase.filter((p) => (p.income_entries || []).some((e) => e.is_retention_claim)).length },
          // Sumber kanonis backend (Asia/Jakarta): overdue_days > 0 = melebihi kontrak.
          { v: "over_contract", label: "Melebihi Kontrak", count: displayedProjectsBase.filter((p) => (p.schedule?.overdue_days || 0) > 0).length },
        ].map((f) => (
          <button
            key={f.v}
            type="button"
            data-testid={`summary-filter-${f.v}`}
            onClick={() => setSummaryFilter(f.v)}
            className={`px-3 py-1 text-[11px] uppercase tracking-wider font-semibold border transition-colors ${summaryFilter === f.v ? (f.v === "combined" ? "bg-indigo-600 text-white border-indigo-600" : f.v === "retention_pending" ? "bg-amber-600 text-white border-amber-600" : "bg-[#0B1D3A] text-white border-[#0B1D3A]") : "bg-white text-slate-600 border-slate-300 hover:border-slate-500"}`}
          >
            {f.label} <span className="tabular-nums opacity-70">({f.count})</span>
          </button>
        ))}
        {summaryFilter !== "all" && (
          <button
            data-testid="summary-filter-clear"
            type="button"
            onClick={() => setSummaryFilter("all")}
            className="text-[10px] text-slate-500 hover:text-[#FF5A1F] underline"
          >
            × Reset
          </button>
        )}
        {/* Status dropdown filter (2026-02-15) — narrow Summary Project by lifecycle status.
            Default "Semua" tampilkan semua status. "Aktif" mencakup awaiting_payment. */}
        <div className="flex items-center gap-1.5" data-testid="status-filter-wrap">
          <span className="overline text-slate-500 text-[10px]">Status</span>
          <select
            data-testid="status-filter-select"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className={`text-[11px] uppercase tracking-wider font-semibold border py-1 pl-2 pr-6 bg-white focus:outline-none focus:border-[#0B1D3A] ${statusFilter === "all" ? "border-slate-300 text-slate-600" : "border-[#0B1D3A] text-[#0B1D3A]"}`}
          >
            <option value="all">Semua Status</option>
            <option value="active">Aktif</option>
            <option value="completed">Selesai</option>
            <option value="cancelled">Dibatalkan</option>
          </select>
          {statusFilter !== "all" && (
            <button
              type="button"
              data-testid="status-filter-clear"
              onClick={() => setStatusFilter("all")}
              className="text-[10px] text-slate-500 hover:text-[#FF5A1F] underline"
              title="Reset status filter"
            >
              × Reset
            </button>
          )}
        </div>
        {/* Preset filter (custom) — user request 2026-02-15 (Clarita session).
            Bookmark curated selection of projects & recall via dropdown. */}
        <ProjectFilterPresetsBar
          projects={projects}
          activePresetId={activePresetId}
          onActivePresetChange={(id) => {
            setActivePresetId(id);
            if (!id) { setPresetProjectIds(null); return; }
            // Cari preset dari cache — component sudah fetch. Query API sekali lagi
            // untuk memastikan project_ids up-to-date (kalau baru dibuat/diedit).
            api.get("/project-filter-presets")
              .then(({ data }) => {
                const found = (data || []).find((p) => p.id === id);
                setPresetProjectIds(found ? (found.project_ids || []) : null);
              })
              .catch(() => setPresetProjectIds(null));
          }}
        />
        {/* Search box — filter tabel by project name / client / WO number / VO name / lokasi.
            Multi-kata AND (contoh: "goodworth jakarta" match project di Jakarta dengan client Goodworth). */}
        <div className="ml-auto relative flex items-center" data-testid="project-search-wrap">
          <Search size={13} className="absolute left-2.5 text-slate-400 pointer-events-none" />
          <input
            data-testid="project-search-input"
            type="text"
            value={searchQ}
            onChange={(e) => setSearchQ(e.target.value)}
            placeholder="Cari project / client / WO / lokasi…"
            className="pl-8 pr-8 py-1.5 border border-slate-300 text-xs w-72 focus:outline-none focus:border-[#0B1D3A] bg-white placeholder:text-slate-400"
          />
          {searchQ && (
            <button
              type="button"
              data-testid="project-search-clear"
              onClick={() => setSearchQ("")}
              title="Bersihkan pencarian"
              className="absolute right-2 text-slate-400 hover:text-[#FF5A1F]"
            >
              <XCircle size={14} />
            </button>
          )}
        </div>
      </div>
      {searchQ && (
        <div className="text-[10px] text-slate-500 -mt-1" data-testid="project-search-summary">
          Menampilkan
          {" "}<span className="font-semibold text-[#0B1D3A]">{filteredProjects.filter((p) => p.project_type === "new_project" && !p.parent_project_id).length}</span> project utama
          {" · "}<span className="font-semibold">{filteredProjects.filter((p) => p.project_type === "internal").length}</span> internal
          {" · "}<span className="font-semibold">{filteredProjects.filter((p) => p.project_type === "new_project" && p.parent_project_id).length}</span> subproject
          {" · "}<span className="font-semibold">{filteredProjects.filter((p) => p.project_type === "variation_order").length}</span> VO
          {" · "}<span className="font-semibold">{filteredProjects.filter((p) => p.project_type === "working_order").length}</span> WO
          {" · "}cocok kata kunci <span className="font-mono text-[#FF5A1F]">"{searchQ}"</span>.
        </div>
      )}

      <section className="bg-white border border-slate-200">
        <table className="w-full text-xs table-auto">
          <thead className="bg-white border-b-2 border-slate-300 ens-sticky-thead shadow-md">
            <tr className="text-left">
              <SortH label="Type" k="type" sort={summarySort} setSort={setSummarySort} className="px-2 py-3" />
              <SortH label="Project" k="name" sort={summarySort} setSort={setSummarySort} className="px-2 py-3" />
              <SortH label="Client" k="client" sort={summarySort} setSort={setSummarySort} className="px-2 py-3" />
              <SortH label="Contract / PO" k="contract" sort={summarySort} setSort={setSummarySort} className="px-2 py-3" />
              <th className="px-2 py-3 overline text-slate-700 whitespace-nowrap bg-white">Retensi</th>
              <th className="px-2 py-3 overline text-slate-700 whitespace-nowrap bg-white">Sisa Penagihan</th>
              <SortH label="Profit" k="profit" sort={summarySort} setSort={setSummarySort} className="px-2 py-3" />
              <SortH label="Sisa Durasi" k="remaining" sort={summarySort} setSort={setSummarySort} className="px-2 py-3" />
              <SortH label="Status" k="status" sort={summarySort} setSort={setSummarySort} className="px-2 py-3" />
              <th className="px-2 py-3 overline text-slate-700 whitespace-nowrap bg-white">Financials</th>
              <th className="px-2 py-3 overline text-slate-700 whitespace-nowrap bg-white">Actions</th>
            </tr>
          </thead>
          <tbody data-testid="projects-table">
            {filteredProjects.length === 0 && <tr><td colSpan={11} className="px-4 py-6 text-center text-slate-400">{summaryFilter === "all" ? "No projects yet." : "Tidak ada proyek yang cocok dengan filter."}</td></tr>}
            {filteredProjects.map((p) => {
              const isVO = p.project_type === "variation_order";
              const isWO = p.project_type === "working_order";
              const isChild = isVO || isWO;
              // Hide child rows until their parent is expanded — TAPI orphan child (parent
              // tidak ada di filteredProjects, mis. parent di-exclude filter atau backend
              // tidak return parent) HARUS tetap tampil supaya user bisa lihat WO/VO-nya.
              // Fix 2026-02-16 (Clarita): sebelumnya orphan hilang total dari Summary.
              const parentInList = p.parent_project_id && filteredIds.has(p.parent_project_id);
              if (isChild && parentInList && !childrenOpen[p.parent_project_id]) return null;
              const isInternal = p.project_type === "internal";
              const kids = childrenByParent[p.id] || [];
              const hasChildren = kids.length > 0;
              const hasWOs = kids.some((k) => k.project_type === "working_order");
              const aggregate = hasChildren && !isChild;
              // Main-has-own-contract-value → shows the "Contract" breakdown row.
              // No contract value on main → treated as an umbrella "Outlet" with only children.
              const mainHasCV = Number(p.contract_value || 0) > 0;
              const showAsContract = aggregate && mainHasCV;
              const showAsOutlet = aggregate && !mainHasCV;
              const childrenExpanded = !!childrenOpen[p.id];
              const status = PROJECT_STATUS[deriveProjectStatus(p)] || PROJECT_STATUS.active;
              // "Contract" aggregate row (main HAS its own contract value) is a pure recap —
              // no edit/close/delete on the header itself; actions belong to the synthetic
              // "Main Contract" breakdown row instead. "Outlet" rows (main without own CV)
              // keep header actions since no separate Main row is rendered.
              const hideHeaderActions = showAsContract;
              const showClose = !isInternal && p.status === "active" && canClose && !hideHeaderActions;
              const showCancel = !isInternal && (p.status === "completed" ? canDeleteCompleted : canCancelActive) && !hideHeaderActions;
              const showReopen = !isInternal && p.status === "completed" && canClose && !hideHeaderActions;
              const showLedger = !isInternal && canLedger && p.status !== "completed";
              const showEdit = !isInternal && (p.status === "completed" ? canEditCompleted : canEditActive) && !hideHeaderActions;
              // Hari berjalan kanonis dari backend (Asia/Jakarta).
              const days = p.schedule?.elapsed_days ?? null;
              // Over Contract Days — user request 09-Feb-2026: "berikan penanda pada status
              // jika sudah melebihi durasi kontrak, sertakan jumlah hari yang berlebih".
              // Rules: proyek AKTIF (bukan completed/cancelled), punya end_date, dan
              // today > end_date. Untuk VO pakai vo_end_date; WO pakai project_done_date
              // fallback ke end_date; lainnya (Main) pakai end_date.
              // Aggregate parent (Contract): use the FURTHEST/latest end date across
              // main.end_date + all VO.vo_end_date + all WO.project_done_date/end_date.
              // Rationale: kontrak dianggap masih aktif selama masih ada komponen (VO/WO/main)
              // yang belum lewat. Kalau main habis 10 hari lagi tapi VO sudah overdue, badge
              // sisa durasi tetap "10 hari" (bukan overdue). User request 10-Feb-2026.
              const childEndFor = (k) => k.project_type === "variation_order" ? k.vo_end_date
                : k.project_type === "working_order" ? (k.project_done_date || k.end_date)
                : k.end_date;
              const aggregateContractEnd = (() => {
                const dates = [p.end_date, ...kids.map(childEndFor)]
                  .filter(Boolean)
                  .map((d) => String(d).slice(0, 10))
                  .filter((s) => s && s.length >= 10);
                if (!dates.length) return null;
                return dates.reduce((a, b) => (a > b ? a : b)); // ISO YYYY-MM-DD lex = chronological
              })();
              const contractEndRaw = isVO
                ? p.vo_end_date
                : (isWO ? (p.project_done_date || p.end_date) : (aggregate ? aggregateContractEnd : p.end_date));
              // Keterlambatan kontrak kanonis dari backend (aggregate-aware:
              // end terjauh induk+anak sudah dihitung engine untuk parent).
              const overContractDays = p.schedule?.overdue_days || 0;
              // Combined-parent lookup: for children (VO/WO), fetch parent's vo_aggregation
              // from displayedProjectsBase to decide whether to hide income/expense actions.
              // For Main Contract synthetic row, use p.vo_aggregation directly (p IS parent).
              const parentDoc = isChild ? displayedProjectsBase.find((x) => x.id === p.parent_project_id) : null;
              const rowUnderCombinedParent = isChild && parentDoc?.vo_aggregation === "combined" && parentDoc?.project_type === "new_project";
              const isExpanded = expanded[p.id];
              // Whether the current viewer is allowed to see the access list panel
              const canSeeAccess = canPerm(user, "user_management", "view");
              // Manage (add / remove) is the same set — kept as an explicit flag for readability.
              const canManageAccess = canSeeAccess;
              const selfInc = p.total_income || 0;
              const selfExp = p.total_expense || 0;
              const selfCv = p.contract_value || (isWO ? (p.po_value || 0) : 0);
              // Retensi (149-ac): formula tergantung retention_type + vo_aggregation.
              // Untuk parent, aggregation = p.vo_aggregation. Untuk child (VO/WO),
              // aggregation di-lookup ke parent (variabel `parentDoc` di atas).
              const selfAgg = isChild
                ? ((parentDoc?.vo_aggregation) || "separate")
                : (p.vo_aggregation || "separate");
              const selfRetentionHeld = computeRetentionHeld(p, selfAgg);
              const selfRetentionClaimed = computeRetentionClaimed(p);
              const selfPphHeld = (p.income_entries || []).reduce((s, ie) => s + (ie.pph_amount != null ? Number(ie.pph_amount) : 0), 0);
              // Aggregate values across main + children (VO + WO) for parent header rows
              const inc = aggregate ? selfInc + kids.reduce((s, k) => s + (k.total_income || 0), 0) : selfInc;
              const exp = aggregate ? selfExp + kids.reduce((s, k) => s + (k.total_expense || 0), 0) : selfExp;
              const cv = aggregate ? selfCv + kids.reduce((s, k) => s + (k.contract_value || 0), 0) : selfCv;
              const retentionHeld = aggregate
                ? selfRetentionHeld + kids.reduce((s, k) => s + computeRetentionHeld(k, selfAgg), 0)
                : selfRetentionHeld;
              const retentionClaimed = aggregate
                ? selfRetentionClaimed + kids.reduce((s, k) => s + computeRetentionClaimed(k), 0)
                : selfRetentionClaimed;
              const retentionOutstanding = Math.max(0, retentionHeld - retentionClaimed);
              // PPN 11% breakdown for row badge — user request 10-Feb-2026.
              // Aggregate row: hitung PPN per komponen (main + tiap child), skip yang Non-PPN.
              // Non-aggregate row: pakai flag project itu sendiri.
              const cvPpnTotal = aggregate
                ? (p.is_ppn_exempt ? 0 : selfCv * 0.11)
                  + kids.reduce((s, k) => s + (k.is_ppn_exempt ? 0 : (Number(k.contract_value) || 0) * 0.11), 0)
                : (p.is_ppn_exempt ? 0 : cv * 0.11);
              const cvWithPpn = cv + cvPpnTotal;
              // For "Main Contract" synthetic row (uses selfCv only)
              const selfCvPpn = p.is_ppn_exempt ? 0 : selfCv * 0.11;
              const selfCvWithPpn = selfCv + selfCvPpn;
              // Bruto sync — user request 10-Feb-2026: Received & Remaining pakai bruto (DPP+PPN)
              // yang sama dengan Contract cell. Multiplier per-komponen (skip Non-PPN).
              const incBruto = aggregate
                ? (p.is_ppn_exempt ? selfInc : selfInc * 1.11)
                  + kids.reduce((s, k) => s + (k.is_ppn_exempt ? (k.total_income || 0) : (k.total_income || 0) * 1.11), 0)
                : (p.is_ppn_exempt ? selfInc : selfInc * 1.11);
              const retentionHeldBruto = aggregate
                ? (p.is_ppn_exempt ? selfRetentionHeld : selfRetentionHeld * 1.11)
                  + kids.reduce((s, k) => {
                      const kRet = computeRetentionHeld(k, selfAgg);
                      return s + (k.is_ppn_exempt ? kRet : kRet * 1.11);
                    }, 0)
                : (p.is_ppn_exempt ? selfRetentionHeld : selfRetentionHeld * 1.11);
              const consumedBruto = incBruto + retentionHeldBruto;
              const remainingBruto = Math.max(0, cvWithPpn - consumedBruto);
              // For "Main Contract" synthetic row
              const selfIncBruto = p.is_ppn_exempt ? selfInc : selfInc * 1.11;
              const selfRetentionHeldBruto = p.is_ppn_exempt ? selfRetentionHeld : selfRetentionHeld * 1.11;
              const pphHeld = aggregate
                ? selfPphHeld + kids.reduce((s, k) => s + (k.income_entries || []).reduce((ss, ie) => ss + (ie.pph_amount != null ? Number(ie.pph_amount) : 0), 0), 0)
                : selfPphHeld;
              const net = inc - exp;
              // Profit margin against nilai kontrak/PO (contract value).
              const profitPct = cv > 0 ? (net / cv) * 100 : null;
              const budgetPct = cv > 0 ? (exp / cv) * 100 : null;
              const overBudget = !isInternal && cv > 0 && exp > cv;
              const nearBudget = !isInternal && cv > 0 && !overBudget && budgetPct >= 80;
              const retPct = Number(p.retention_pct) || 0;
              const retentionPeriodDays = Number(p.retention_period_days) || 0;
              // Retention reminder: for completed projects with outstanding retention.
              const isCompletedWithRetention = p.status === "completed" && retentionOutstanding > 0.01;
              let retentionDueDate = null;
              let retentionOverdue = false;
              if (isCompletedWithRetention && retentionPeriodDays > 0 && p.completed_at) {
                const dueDate = new Date(p.completed_at);
                dueDate.setDate(dueDate.getDate() + retentionPeriodDays);
                retentionDueDate = dueDate;
                retentionOverdue = new Date() > dueDate;
              }
              // Net received = income − pph − retention (retention IS deducted from cash flow)
              const netReceived = inc - pphHeld - retentionHeld;
              // Remaining billing quota = contract − (invoice consumed) where invoice consumed = amount + retention
              const consumedFromContract = inc + retentionHeld;
              const remaining = cv > 0 ? Math.max(0, cv - consumedFromContract) : null;
              // 2026-10-22 (user request "SINKRONISASI PROGRESS PEMBAYARAN RESMI"):
              // Progres pembayaran menggunakan SATU engine kanonis
              // (payment_breakdown.official_payment_progress). Hindari
              // pembilang `total_income` yg mencampur DP/retensi/legacy/dsb.
              const bdOffPct = p?.payment_breakdown?.official_payment_progress;
              const bdOffReceived = p?.payment_breakdown?.received_official;
              const bdOffBase = p?.payment_breakdown?.official_collectible_amount;
              const bdReceiptOver = p?.payment_breakdown?.receipt_over_official;
              const receivedPct = (bdOffPct !== undefined && bdOffPct !== null)
                ? bdOffPct
                : (cv > 0 ? (inc / cv) * 100 : null);
              const isVOChild = isChild;
              return (
                <FragmentRow key={p.id}>
                  <tr className={`border-b border-slate-100 hover:bg-slate-50 cursor-pointer ${overBudget ? "bg-rose-50/40 border-l-4 border-l-rose-500" : ""}`} onClick={() => !isInternal && toggleExpand(p.id)}>
                      <td className="px-2 py-2">
                      {(isInternal || isVO || isWO || aggregate) ? (
                        <span className="px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider bg-white text-slate-700 border border-slate-300">
                          {isInternal ? "Internal" : isVO ? "VO" : isWO ? "WO" : showAsOutlet ? "Outlet" : "Contract"}
                        </span>
                      ) : (
                        <span className="text-slate-300">—</span>
                      )}
                    </td>
                      <td className="px-2 py-2">
                      <div className={`${isVOChild ? "pl-6 border-l-2 " + (isVO ? "border-[#FF5A1F]" : "border-sky-500") : ""}`}>
                        <div className="font-semibold text-[#0B1D3A] flex items-center gap-1.5">
                          {hasChildren && !isChild && (
                            <button
                              type="button"
                              data-testid={`toggle-children-${p.id}`}
                              onClick={(e) => { e.stopPropagation(); toggleChildren(p.id); }}
                              title={childrenExpanded ? "Hide VO/WO" : "Show VO/WO"}
                              className="text-slate-500 hover:text-[#0B1D3A]"
                            >
                              {childrenExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                            </button>
                          )}
                          {isVOChild && <span className={`text-[10px] mr-1 ${isVO ? "text-[#FF5A1F]" : "text-sky-600"}`}>↳</span>}
                          <span>{p.project_name || "-"}</span>
                          {hasChildren && !isChild && (
                            <span className="text-[10px] font-semibold px-1 py-0.5 bg-slate-100 text-slate-600 border border-slate-300 tabular-nums" data-testid={`children-count-${p.id}`}>
                              {kids.length} sub
                            </span>
                          )}
                          {/* COMBINED badge — muncul di parent New Project yang aktif mode
                              "Perhitungan VO Digabung". Membantu BOD tahu tanpa buka detail
                              bahwa income/expense semua VO/WO ter-merge ke parent ini +
                              billing limit gabungan. User request 09-Feb-2026. */}
                          {p.project_type === "new_project" && p.vo_aggregation === "combined" && !isChild && (
                            <span
                              data-testid={`combined-badge-${p.id}`}
                              title="Perhitungan VO Digabung — income/expense semua VO/WO merge ke parent ini, limit tagihan = total kontrak semua"
                              className="text-[9px] font-bold px-1.5 py-0.5 bg-white text-slate-700 border border-slate-300 uppercase tracking-wider"
                            >
                              Combined
                            </span>
                          )}
                        </div>
                        {isVO && <div className="text-xs text-slate-600 mt-0.5">{p.variation_order_name}</div>}
                        {isWO && (
                          <div className="text-xs text-slate-600 mt-0.5">
                            WO {p.wo_number}{p.po_number ? ` · PO ${p.po_number}` : ""}
                            {p.wo_title && <div className="text-[11px] text-sky-700 font-medium mt-0.5" data-testid={`wo-title-${p.id}`}>{p.wo_title}</div>}
                          </div>
                        )}
                      </div>
                    </td>
                      <td className="px-2 py-2">{p.client || "-"}</td>
                      <td className="px-2 py-2 tabular-nums whitespace-nowrap">
                      {cv > 0 ? (
                        <div>
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span data-testid={`cv-value-${p.id}`}>{formatIDR(cvWithPpn)}</span>
                            {retPct > 0 && (
                              <span className="text-[9px] font-semibold px-1 py-0.5 bg-white text-slate-700 border border-slate-300" data-testid={`retention-badge-${p.id}`}>
                                RET {retPct}%
                              </span>
                            )}
                            {p.is_ppn_exempt && !aggregate && (
                              <span
                                data-testid={`non-ppn-badge-${p.id}`}
                                title="Project ini bebas PPN 11%"
                                className="text-[9px] font-semibold px-1 py-0.5 bg-slate-100 text-slate-600 border border-slate-300 italic"
                              >
                                Non-PPN
                              </span>
                            )}
                          </div>
                          <div className="text-[10px] text-slate-500 mt-0.5">
                            Received <span className="font-semibold text-emerald-700 tabular-nums">{formatIDR(incBruto)}</span>
                            {" · "}
                            Remaining <span className="font-semibold text-amber-700 tabular-nums" data-testid={`remaining-${p.id}`}>{formatIDR(remainingBruto)}</span>
                          </div>
                        </div>
                      ) : "-"}
                    </td>
                      {/* Retensi cell — user request 2026-02-10. Menampilkan max retensi
                          berdasar retention_type: inside=sum(retention_amount stored),
                          outside=contract × r/100. Outstanding = max − claimed. */}
                      <td className="px-2 py-2 whitespace-nowrap" data-testid={`retensi-cell-${p.id}`}>
                        {(() => {
                          if (isInternal || retPct <= 0) return <span className="text-slate-300">—</span>;
                          const rTypeOwn = p.retention_type || "inside";
                          // Retention max = for outside: contract × r/100. For inside: sum(retention_amount).
                          // Aggregate: sum across parent + kids per-type.
                          const ownMax = rTypeOwn === "outside" && retPct > 0 && retPct < 100
                            ? selfCv * retPct / 100
                            : selfRetentionHeld;
                          const retentionMax = aggregate
                            ? ownMax + kids.reduce((s, k) => {
                                const krp = Number(k.retention_pct) || 0;
                                const krt = k.retention_type || "inside";
                                if (krt === "outside" && krp > 0 && krp < 100) return s + (Number(k.contract_value) || 0) * krp / 100;
                                return s + computeRetentionHeld(k, selfAgg);
                              }, 0)
                            : ownMax;
                          const outstanding = Math.max(0, retentionMax - retentionClaimed);
                          const hasClaim = retentionClaimed > 0.01;
                          return (
                            <div className="flex flex-col gap-0.5 items-start">
                              <div className="flex items-center gap-1">
                                <span className="text-[9px] font-semibold px-1 py-0.5 bg-white text-slate-700 border border-slate-300 tabular-nums">
                                  {retPct}% {rTypeOwn === "outside" ? "AKHIR" : "PER TAGIHAN"}
                                </span>
                              </div>
                              <div className="text-[10px] tabular-nums">
                                {retentionMax > 0 ? (
                                  <>
                                    <span className={hasClaim ? "text-emerald-700 font-semibold" : "text-amber-700 font-semibold"}>{formatIDR(retentionMax)}</span>
                                    {hasClaim && <span className="text-[9px] text-emerald-600 ml-1">✓ Claimed</span>}
                                    {!hasClaim && outstanding > 0 && p.status === "completed" && <span className="text-[9px] text-amber-600 ml-1">outstanding</span>}
                                  </>
                                ) : <span className="text-slate-400">—</span>}
                              </div>
                            </div>
                          );
                        })()}
                      </td>
                      {/* Sisa Penagihan — user request 2026-02-10. Formula:
                          if retention claim done: 0%. Else: (cv - regular_inc)/cv * 100.
                          Contoh: cv=10M, ret 5%, billed 9.5M → sisa 5% (5% ret masih blm ditagih). */}
                      <td className="px-2 py-2 whitespace-nowrap" data-testid={`sisa-penagihan-${p.id}`}>
                        {(() => {
                          if (isInternal || cv <= 0) return <span className="text-slate-300">—</span>;
                          const hasClaim = retentionClaimed > 0.01;
                          if (hasClaim) {
                            return (
                              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-semibold border border-emerald-300 bg-emerald-50 text-emerald-700 tabular-nums" title="Retention claim sudah ditagihkan — semua tagihan selesai">
                                0% <span className="text-emerald-500">✓</span>
                              </span>
                            );
                          }
                          const regularInc = Math.max(0, inc - retentionClaimed);
                          const sisaPct = cv > 0 ? Math.max(0, ((cv - regularInc) / cv) * 100) : 0;
                          const sisaRp = Math.max(0, cv - regularInc);
                          const cls = sisaPct >= 50
                            ? "border-slate-300 bg-white text-slate-700"
                            : sisaPct >= 20
                            ? "border-amber-300 bg-amber-50 text-amber-700"
                            : sisaPct > 0
                            ? "border-emerald-300 bg-emerald-50 text-emerald-700"
                            : "border-slate-300 bg-slate-50 text-slate-500";
                          return (
                            <div className="flex flex-col gap-0.5 items-start">
                              <span className={`inline-flex items-center px-1.5 py-0.5 text-[10px] font-semibold border tabular-nums ${cls}`} title={`Sisa ${formatIDR(sisaRp)} dari kontrak ${formatIDR(cv)}`}>
                                {sisaPct.toFixed(1)}%
                              </span>
                              <span className="text-[9px] text-slate-500 tabular-nums">{formatIDR(sisaRp)}</span>
                            </div>
                          );
                        })()}
                      </td>
                    <td className="px-2 py-2 tabular-nums whitespace-nowrap">
                      {(() => {
                        // R227: angka & label kanonis dari profit_view engine —
                        // identik dgn Monitoring & Cost Control. Proyek tanpa
                        // data CC → "Belum Dapat Dipastikan" (bukan margin 100%).
                        const pv = ccSummaries[p.id]?.profit_view || null;
                        if (pv) {
                          const st = pv.completeness?.status || "unknown";
                          const selisih = Number(pv.selisih || 0);
                          const hasCc = (pv.revenue_basis?.item_count || 0) > 0;
                          if (!hasCc && pv.completeness?.status === "unknown") {
                            return (
                              <button type="button" data-testid={`profit-idr-${p.id}`} onClick={() => setProfitDrillPid(p.id)}
                                className="inline-flex flex-col items-start gap-0.5 text-left" title="Tabel Cost Control kosong — kelengkapan biaya belum dapat dipastikan">
                                <span className="text-[10px] text-slate-400 italic">Belum Dapat Dipastikan</span>
                                <ProfitStatusBadge view={pv} small testid={`profit-badge-${p.id}`} />
                              </button>
                            );
                          }
                          return (
                            <div className="flex flex-col gap-0.5">
                              <button type="button" onClick={() => setProfitDrillPid(p.id)}
                                className={`px-1.5 py-0.5 text-[11px] font-semibold border tabular-nums inline-flex text-left ${
                                  st === "complete"
                                    ? (selisih >= 0 ? "bg-emerald-50 border-emerald-300 text-emerald-700" : "bg-rose-50 border-rose-300 text-rose-700")
                                    : st === "incomplete"
                                      ? "bg-amber-50 border-amber-300 text-amber-800"
                                      : "bg-slate-50 border-slate-300 text-slate-600"
                                }`}
                                data-testid={`profit-idr-${p.id}`}
                                title={`${pv.labels?.selisih || "Selisih Sementara"} — klik untuk rincian`}>
                                {selisih >= 0 ? "+" : "−"}{formatIDR(Math.abs(selisih))}
                                {st !== "complete" && <span className="ml-1 opacity-70 normal-case">(sementara)</span>}
                              </button>
                              <span
                                className={`text-[10px] tabular-nums ${pv.margin === null ? "text-slate-400" : st === "complete" ? (pv.margin >= 0 ? "text-emerald-600" : "text-rose-600") : "text-amber-700"}`}
                                data-testid={`profit-pct-${p.id}`}
                              >
                                {pv.margin === null
                                  ? "Margin belum dapat dihitung"
                                  : <>{pv.margin >= 0 ? "+" : ""}{pv.margin.toFixed(1)}% <span className="text-slate-400">{st === "complete" ? "margin" : "margin sementara"}</span></>}
                              </span>
                            </div>
                          );
                        }
                        // Fallback lama bila summaries belum termuat.
                        return isInternal ? (
                          <span className="text-slate-300">—</span>
                        ) : inc <= 0 && exp <= 0 ? (
                          <span className="text-slate-300">—</span>
                        ) : (
                          <div className="flex flex-col gap-0.5">
                            <span
                              className={`px-1.5 py-0.5 text-[11px] font-semibold border tabular-nums inline-flex ${
                                net >= 0
                                  ? "bg-emerald-50 border-emerald-300 text-emerald-700"
                                  : "bg-rose-50 border-rose-300 text-rose-700"
                              }`}
                              data-testid={`profit-idr-${p.id}`}
                            >
                              {net >= 0 ? "+" : "−"}{formatIDR(Math.abs(net))}
                            </span>
                            {profitPct != null && (
                              <span
                                className={`text-[10px] tabular-nums ${profitPct >= 0 ? "text-emerald-600" : "text-rose-600"}`}
                                data-testid={`profit-pct-${p.id}`}
                              >
                                {profitPct >= 0 ? "+" : ""}{profitPct.toFixed(1)}% <span className="text-slate-400">of contract</span>
                              </span>
                            )}
                          </div>
                        );
                      })()}
                    </td>
                      <td className="px-2 py-2 whitespace-nowrap" data-testid={`remaining-days-${p.id}`}>
                        {/* Sisa durasi kanonis backend (Asia/Jakarta) + drill-down rincian jadwal. */}
                        <SisaWaktuBadge schedule={p.schedule} testid={`remaining-days-badge-${p.id}`} />
                      </td>
                      <td className="px-2 py-2">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className={`px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider border ${status.cls}`}>{status.label}</span>
                        {overContractDays > 0 && (
                          <span
                            data-testid={`over-contract-${p.id}`}
                            title={`Kontrak berakhir ${new Date(contractEndRaw).toLocaleDateString("id-ID")} — sudah lewat ${overContractDays} hari`}
                            className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider border border-red-600 bg-red-600 text-white animate-pulse"
                          >
                            <AlertTriangle size={10} className="-mt-px" />
                            Over {overContractDays}d
                          </span>
                        )}
                        {overBudget && (
                          <span data-testid={`over-budget-${p.id}`} className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider border border-rose-400 bg-rose-500 text-white">
                            <AlertTriangle size={10} className="-mt-px" />
                            Over Budget
                          </span>
                        )}
                        {nearBudget && (
                          <span data-testid={`near-budget-${p.id}`} className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider border border-amber-400 bg-amber-100 text-amber-800">
                            <AlertTriangle size={10} className="-mt-px" />
                            {budgetPct.toFixed(0)}% Used
                          </span>
                        )}
                        {isCompletedWithRetention && (
                          <span
                            data-testid={`retention-reminder-${p.id}`}
                            title={`Retensi Rp ${retentionOutstanding.toLocaleString("id-ID")} belum diklaim${retentionDueDate ? ` · jatuh tempo ${retentionDueDate.toLocaleDateString("id-ID")}` : ""}`}
                            className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider border ${
                              retentionOverdue
                                ? "border-rose-400 bg-rose-500 text-white animate-pulse"
                                : "border-amber-400 bg-amber-100 text-amber-800"
                            }`}
                          >
                            <AlertTriangle size={10} className="-mt-px" />
                            {retentionOverdue ? "Retensi Terlambat" : "Retensi Menunggu"}
                          </span>
                        )}
                      </div>
                    </td>
                      <td className="px-2 py-2 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                      {isInternal ? (
                        // Internal projects (Office Medan, Head Office) — expense-only.
                        // User request 2026-02-10: bisa input pengeluaran, tapi tidak uang masuk.
                        <div className="flex gap-1.5">
                          <span className="px-2 py-1 text-[10px] uppercase tracking-wider font-semibold border border-slate-200 bg-slate-50 text-slate-400 italic cursor-not-allowed" title="Internal project — tidak ada uang masuk">
                            <TrendingUp size={11} className="inline -mt-0.5 mr-1" />—
                          </span>
                          <button data-testid={`btn-expense-${p.id}`} onClick={() => setExpDlg({ open: true, project: p })} className="px-2 py-1 text-[10px] uppercase tracking-wider font-semibold border border-rose-300 bg-rose-50 text-rose-700 hover:bg-rose-100 tabular-nums" title="Input pengeluaran internal">
                            <TrendingDown size={11} className="inline -mt-0.5 mr-1" />{formatIDR(exp)}
                          </button>
                        </div>
                      ) : rowUnderCombinedParent ? (
                        <span
                          data-testid={`combined-child-hint-${p.id}`}
                          title="Uang masuk & keluar dikelola terpusat di parent (VO Digabung)"
                          className="px-2 py-1 text-[10px] uppercase tracking-wider font-semibold border border-slate-300 bg-white text-slate-600 italic"
                        >
                          Digabung di parent
                        </span>
                      ) : (
                        <div className="flex gap-1.5">
                          {canViewLedger && !aggregate && (
                            <>
                              <button data-testid={`btn-income-${p.id}`} onClick={() => setIncDlg({ open: true, project: p })} className="px-2 py-1 text-[10px] uppercase tracking-wider font-semibold border border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 tabular-nums">
                                <TrendingUp size={11} className="inline -mt-0.5 mr-1" />{formatIDR(inc)}
                              </button>
                              <button data-testid={`btn-expense-${p.id}`} onClick={() => setExpDlg({ open: true, project: p })} className="px-2 py-1 text-[10px] uppercase tracking-wider font-semibold border border-rose-300 bg-rose-50 text-rose-700 hover:bg-rose-100 tabular-nums">
                                <TrendingDown size={11} className="inline -mt-0.5 mr-1" />{formatIDR(exp)}
                              </button>
                              {/* 149-cw-r120 (2026-08-29): AccurateUpdateMenu dihapus — input keuangan hanya dari Laporan Keuangan. */}
                            </>
                          )}
                          {canViewLedger && aggregate && (
                            <div className="flex gap-1.5 tabular-nums" data-testid={`agg-totals-${p.id}`}>
                              {p.vo_aggregation === "combined" && p.project_type === "new_project" ? (
                                <>
                                  <button data-testid={`btn-income-${p.id}`} onClick={() => setIncDlg({ open: true, project: p })} className="px-2 py-1 text-[10px] uppercase tracking-wider font-semibold border border-emerald-400 bg-emerald-100 text-emerald-800 hover:bg-emerald-200 tabular-nums" title="Combined mode — merged dari parent + semua VO/WO">
                                    <TrendingUp size={11} className="inline -mt-0.5 mr-1" />{formatIDR(inc)}
                                  </button>
                                  <button data-testid={`btn-expense-${p.id}`} onClick={() => setExpDlg({ open: true, project: p })} className="px-2 py-1 text-[10px] uppercase tracking-wider font-semibold border border-rose-400 bg-rose-100 text-rose-800 hover:bg-rose-200 tabular-nums" title="Combined mode — merged dari parent + semua VO/WO">
                                    <TrendingDown size={11} className="inline -mt-0.5 mr-1" />{formatIDR(exp)}
                                  </button>
                                </>
                              ) : (
                                <>
                                  <span className="px-2 py-1 text-[10px] uppercase tracking-wider font-semibold border border-slate-300 bg-white text-slate-700">
                                    <TrendingUp size={11} className="inline -mt-0.5 mr-1" />{formatIDR(inc)}
                                  </span>
                                  <span className="px-2 py-1 text-[10px] uppercase tracking-wider font-semibold border border-slate-300 bg-white text-slate-700">
                                    <TrendingDown size={11} className="inline -mt-0.5 mr-1" />{formatIDR(exp)}
                                  </span>
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </td>
                      <td className="px-2 py-2 flex gap-2 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                      {showEdit && (
                        <Button data-testid={`btn-edit-${p.id}`} onClick={() => setEditDlg({ open: true, project: p })} size="sm" variant="outline" className="h-7 rounded-none text-xs border-slate-300 hover:bg-slate-50">
                          <Pencil size={12} className="mr-1" /> Edit
                        </Button>
                      )}
                      {showClose && <Button data-testid={`btn-close-${p.id}`} onClick={() => setCloseDlg({ open: true, project: p })} size="sm" className="h-7 rounded-none bg-[#0B1D3A] hover:bg-[#1a2e52] text-xs">Close</Button>}
                      {!isInternal && (
                        <Button data-testid={`btn-lifecycle-${p.id}`} onClick={() => setLifecycleDlg({ open: true, project: p })} size="sm" variant="outline" className="h-7 rounded-none text-xs border-amber-400 text-amber-700 hover:bg-amber-50" title="Kelola Lifecycle: Aktif → Closing → Closed">
                          Lifecycle
                        </Button>
                      )}
                      {showReopen && (
                        <Button
                          data-testid={`btn-reopen-${p.id}`}
                          onClick={() => reopen(p.id)}
                          size="sm"
                          variant="outline"
                          className="h-7 rounded-none text-xs text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                        >
                          Reopen
                        </Button>
                      )}
                      {showCancel && (
                        <Button
                          data-testid={`btn-cancel-${p.id}`}
                          onClick={() => cancel(p.id, p.status === "completed")}
                          size="sm"
                          variant="outline"
                          className="h-7 rounded-none text-xs text-rose-600 border-rose-300 hover:bg-rose-50"
                        >
                          <XCircle size={12} className="mr-1" />
                          {p.status === "completed" ? "Hapus" : "Batalkan"}
                        </Button>
                      )}
                      {isInternal && <span className="text-[10px] text-slate-400 italic" title="Internal project — hanya bisa input pengeluaran, tidak ada uang masuk">Expense-only</span>}
                      <AuditLogButton feature_key="project" target_id={p.id} title={p.project_name || p.variation_order_name || p.wo_number || "Project"} size="sm" />
                    </td>
                  </tr>

                  {showAsContract && childrenExpanded && (
                    <tr className="border-b border-slate-100 bg-slate-50/40" data-testid={`row-main-self-${p.id}`}>
                    <td className="px-2 py-2">
                        <span className="px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider bg-white text-slate-700 border border-slate-300 whitespace-nowrap">Main Contract</span>
                      </td>
                    <td className="px-2 py-2">
                        <div className="pl-6 border-l-2 border-[#0B1D3A]">
                          <div className="text-[10px] mr-1 text-[#0B1D3A]">↳ Kontrak Induk</div>
                          <div className="font-medium text-slate-700 text-sm">{p.project_name}</div>
                        </div>
                      </td>
                    <td className="px-2 py-2 text-slate-500 text-xs">{p.client || "-"}</td>
                    <td className="px-2 py-2 tabular-nums whitespace-nowrap text-xs">
                        {selfCv > 0 ? (
                          <div>
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <span data-testid={`cv-value-main-${p.id}`}>{formatIDR(selfCvWithPpn)}</span>
                              {retPct > 0 && <span className="text-[9px] font-semibold px-1 py-0.5 bg-white text-slate-700 border border-slate-300">RET {retPct}%</span>}
                              {p.is_ppn_exempt && (
                                <span className="text-[9px] font-semibold px-1 py-0.5 bg-slate-100 text-slate-600 border border-slate-300 italic" title="Main contract Non-PPN">Non-PPN</span>
                              )}
                            </div>
                            {(() => {
                              const selfConsumedBruto = selfIncBruto + selfRetentionHeldBruto;
                              const selfRemainingBruto = Math.max(0, selfCvWithPpn - selfConsumedBruto);
                              return (
                                <div className="text-[10px] text-slate-500 mt-0.5">
                                  Received <span className="font-semibold text-emerald-700 tabular-nums" data-testid={`received-main-${p.id}`}>{formatIDR(selfIncBruto)}</span>
                                  {" · "}
                                  Remaining <span className="font-semibold text-amber-700 tabular-nums" data-testid={`remaining-main-${p.id}`}>{formatIDR(selfRemainingBruto)}</span>
                                </div>
                              );
                            })()}
                          </div>
                        ) : "-"}
                      </td>
                    <td className="px-2 py-2 whitespace-nowrap" data-testid={`retensi-cell-main-${p.id}`}>
                      {(() => {
                        if (retPct <= 0) return <span className="text-slate-300">—</span>;
                        const rTypeOwn = p.retention_type || "inside";
                        const retMax = rTypeOwn === "outside" && retPct > 0 && retPct < 100
                          ? selfCv * retPct / 100
                          : selfRetentionHeld;
                        const hasClaim = selfRetentionClaimed > 0.01;
                        return (
                          <div className="flex flex-col gap-0.5 items-start">
                            <span className="text-[9px] font-semibold px-1 py-0.5 bg-white text-slate-700 border border-slate-300 tabular-nums">
                              {retPct}% {rTypeOwn === "outside" ? "AKHIR" : "PER TAGIHAN"}
                            </span>
                            <span className="text-[10px] tabular-nums">
                              {retMax > 0 ? (
                                <span className={hasClaim ? "text-emerald-700 font-semibold" : "text-amber-700 font-semibold"}>{formatIDR(retMax)}{hasClaim && <span className="ml-1 text-[9px] text-emerald-600">✓</span>}</span>
                              ) : <span className="text-slate-400">—</span>}
                            </span>
                          </div>
                        );
                      })()}
                    </td>
                    <td className="px-2 py-2 whitespace-nowrap" data-testid={`sisa-penagihan-main-${p.id}`}>
                      {(() => {
                        if (selfCv <= 0) return <span className="text-slate-300">—</span>;
                        const hasClaim = selfRetentionClaimed > 0.01;
                        if (hasClaim) return <span className="inline-flex px-1.5 py-0.5 text-[10px] font-semibold border border-emerald-300 bg-emerald-50 text-emerald-700 tabular-nums">0% <span className="ml-1 text-emerald-500">✓</span></span>;
                        const regularInc = Math.max(0, selfInc - selfRetentionClaimed);
                        const sisaPct = ((selfCv - regularInc) / selfCv) * 100;
                        const sisaRp = Math.max(0, selfCv - regularInc);
                        const cls = sisaPct >= 50 ? "border-slate-300 bg-white text-slate-700"
                          : sisaPct >= 20 ? "border-amber-300 bg-amber-50 text-amber-700"
                          : sisaPct > 0 ? "border-emerald-300 bg-emerald-50 text-emerald-700"
                          : "border-slate-300 bg-slate-50 text-slate-500";
                        return (
                          <div className="flex flex-col gap-0.5 items-start">
                            <span className={`inline-flex px-1.5 py-0.5 text-[10px] font-semibold border tabular-nums ${cls}`}>{Math.max(0, sisaPct).toFixed(1)}%</span>
                            <span className="text-[9px] text-slate-500 tabular-nums">{formatIDR(sisaRp)}</span>
                          </div>
                        );
                      })()}
                    </td>
                    <td className="px-2 py-2 tabular-nums whitespace-nowrap text-xs">
                        {selfCv > 0 ? (
                          <span className={`px-1.5 py-0.5 text-[10px] font-semibold border tabular-nums ${(selfInc - selfExp) >= 0 ? "bg-emerald-50 border-emerald-300 text-emerald-700" : "bg-rose-50 border-rose-300 text-rose-700"}`}>
                            {(((selfInc - selfExp) / selfCv) * 100).toFixed(1)}%
                          </span>
                        ) : <span className="text-slate-300">—</span>}
                      </td>
                    <td className="px-2 py-2 whitespace-nowrap" data-testid={`remaining-main-duration-${p.id}`}>
                        {/* Sisa durasi kanonis backend (Asia/Jakarta) + drill-down rincian jadwal. */}
                        <SisaWaktuBadge schedule={p.schedule} testid={`remaining-main-badge-${p.id}`} />
                      </td>
                    {/* Status cell — user request 10-Feb-2026: samakan dengan pola VO
                        (Active kalau belum close & belum lewat; Overdate kalau end_date sudah lewat;
                        Close kalau completed/cancelled). Pakai deriveProjectStatus supaya konsisten
                        dengan parent row & row-VO/WO. */}
                    <td className="px-2 py-2 whitespace-nowrap" data-testid={`status-main-${p.id}`}>
                      {(() => {
                        const st = PROJECT_STATUS[deriveProjectStatus(p)] || PROJECT_STATUS.active;
                        return (
                          <span className={`px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider border ${st.cls}`}>{st.label}</span>
                        );
                      })()}
                    </td>
                    <td className="px-2 py-2 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                        {canViewLedger && (
                          p.vo_aggregation === "combined" && p.project_type === "new_project" ? (
                            <span
                              data-testid={`combined-main-hint-${p.id}`}
                              title="Uang masuk & keluar dikelola terpusat di parent (VO Digabung). Klik tombol pada baris header parent."
                              className="px-2 py-1 text-[10px] uppercase tracking-wider font-semibold border border-slate-300 bg-white text-slate-600 italic"
                            >
                              Digabung di parent
                            </span>
                          ) : (
                            <div className="flex gap-1.5">
                              <button data-testid={`btn-income-main-${p.id}`} onClick={() => setIncDlg({ open: true, project: p })} className="px-2 py-1 text-[10px] uppercase tracking-wider font-semibold border border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 tabular-nums">
                                <TrendingUp size={11} className="inline -mt-0.5 mr-1" />{formatIDR(selfInc)}
                              </button>
                              <button data-testid={`btn-expense-main-${p.id}`} onClick={() => setExpDlg({ open: true, project: p })} className="px-2 py-1 text-[10px] uppercase tracking-wider font-semibold border border-rose-300 bg-rose-50 text-rose-700 hover:bg-rose-100 tabular-nums">
                                <TrendingDown size={11} className="inline -mt-0.5 mr-1" />{formatIDR(selfExp)}
                              </button>
                              {/* 149-cw-r120 (2026-08-29): AccurateUpdateMenu dihapus di baris parent aggregate juga. */}
                            </div>
                          )
                        )}
                      </td>
                    <td className="px-2 py-2 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                        <div className="flex gap-2">
                          {(p.status === "completed" ? canEditCompleted : canEditActive) && (
                            <Button
                              data-testid={`btn-edit-main-${p.id}`}
                              onClick={() => setEditDlg({ open: true, project: p })}
                              size="sm"
                              variant="outline"
                              className="h-7 rounded-none text-xs border-slate-300 hover:bg-slate-50"
                            >
                              <Pencil size={12} className="mr-1" /> Edit
                            </Button>
                          )}
                          {p.status === "active" && canClose && (
                            <Button data-testid={`btn-close-main-${p.id}`} onClick={() => setCloseDlg({ open: true, project: p })} size="sm" className="h-7 rounded-none bg-[#0B1D3A] hover:bg-[#1a2e52] text-xs">
                              Close
                            </Button>
                          )}
                          {p.status === "completed" && canClose && (
                            <Button
                              data-testid={`btn-reopen-main-${p.id}`}
                              onClick={() => reopen(p.id)}
                              size="sm"
                              variant="outline"
                              className="h-7 rounded-none text-xs text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                            >
                              Reopen
                            </Button>
                          )}
                          {((p.status === "completed" && canDeleteCompleted) || (p.status !== "completed" && canCancelActive)) && (
                            <Button
                              data-testid={`btn-cancel-main-${p.id}`}
                              onClick={() => cancel(p.id, p.status === "completed")}
                              size="sm"
                              variant="outline"
                              className="h-7 rounded-none text-xs text-rose-600 border-rose-300 hover:bg-rose-50"
                            >
                              <XCircle size={12} className="mr-1" />
                              {p.status === "completed" ? "Hapus" : "Batalkan"}
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}

                  {isExpanded && !isInternal && (
                    <tr className="bg-slate-50/60 border-b border-slate-100">
                      <td colSpan={11} className="px-6 py-4">
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-xs">
                          <FinCell label="Location" value={[p.city, p.province].filter(Boolean).join(", ") || "-"} />
                          {isWO && (
                            <>
                              <FinCell label="WO / PO" value={<>{p.wo_number || "-"}<div className="text-[10px] text-slate-500 font-normal">{p.po_number ? `PO ${p.po_number}` : ""}</div></>} tone="text-sky-800" />
                              <FinCell label="PO Value" value={p.po_value != null ? formatIDR(p.po_value) : "-"} tone="text-sky-700" />
                              <FinCell
                                label="Contract Value"
                                value={p.contract_value != null ? formatIDR(p.contract_value) : "-"}
                                tone="text-[#0B1D3A]"
                              />
                              <FinCell
                                label="Retention"
                                value={retPct > 0 ? `${retPct}%` : "None"}
                                tone={retPct > 0 ? "text-amber-700" : "text-slate-500"}
                              />
                              <FinCell label="SPH Submit" value={fmtDate(p.sph_submit_date) || "—"} />
                              <FinCell label="Site Survey" value={fmtDate(p.site_survey_date) || "—"} />
                              <FinCell label="Project Done" value={fmtDate(p.project_done_date) || "—"} />
                              <FinCell label="Invoice Sent" value={fmtDate(p.invoice_sent_date) || "—"} />
                            </>
                          )}
                          <FinCell
                            label={isVO ? "VO Period" : isWO ? "Work Period" : "Period"}
                            value={isWO
                              ? `${fmtDate(p.wo_date)} → ${fmtDate(p.project_done_date) || "-"}`
                              : `${fmtDate(isVO ? p.vo_start_date : p.start_date)} → ${fmtDate(isVO ? p.vo_end_date : p.end_date)}`
                            }
                          />
                          <FinCell label="Net" value={formatIDR(net)} tone={net >= 0 ? "text-emerald-700" : "text-rose-700"} />
                          <FinCell
                            label="Profit % (of contract)"
                            value={profitPct != null ? `${profitPct >= 0 ? "+" : ""}${profitPct.toFixed(2)}%` : "-"}
                            tone={profitPct != null && profitPct < 0 ? "text-rose-700" : "text-emerald-700"}
                          />
                          {isCompletedWithRetention && (
                            <FinCell
                              label={retentionOverdue ? "Retention Overdue" : "Retention Pending"}
                              value={<>
                                {formatIDR(retentionOutstanding)}
                                {retentionDueDate && (
                                  <div className="text-[10px] text-slate-500 font-normal">
                                    {retentionOverdue ? "Jatuh tempo " : "Jatuh tempo "}{retentionDueDate.toLocaleDateString("id-ID", { day: "numeric", month: "short", year: "numeric" })}
                                  </div>
                                )}
                                {!retentionDueDate && retentionPeriodDays === 0 && (
                                  <div className="text-[10px] text-slate-500 font-normal">Set masa retensi (hari) di Edit Project</div>
                                )}
                              </>}
                              tone={retentionOverdue ? "text-rose-700" : "text-amber-700"}
                            />
                          )}
                          <FinCell
                            label="Budget Used"
                            value={budgetPct != null ? `${budgetPct.toFixed(1)}%${overBudget ? " · OVER" : ""}` : "-"}
                            tone={overBudget ? "text-rose-700" : nearBudget ? "text-amber-700" : "text-slate-700"}
                          />
                          <FinCell
                            label="Received"
                            value={cv > 0 ? <>{formatIDR(bdOffReceived ?? inc)}<div className={`text-[10px] ${bdReceiptOver ? "text-rose-700 font-semibold" : "text-slate-500"} font-normal`}>{receivedPct != null ? receivedPct.toFixed(1) : "—"}% {bdOffPct != null ? "resmi" : "of contract"}{bdReceiptOver ? " · PERLU VERIFIKASI" : ""}</div></> : "-"}
                            tone={bdReceiptOver ? "text-rose-700" : "text-emerald-700"}
                          />
                          <FinCell
                            label="Remaining Billable"
                            value={remaining != null ? formatIDR(remaining) : "-"}
                            tone={remaining != null && remaining > 0 ? "text-amber-700" : "text-slate-700"}
                          />
                          {(retPct > 0 || pphHeld > 0) && (
                            <>
                              {pphHeld > 0 && (
                                <FinCell
                                  label="Income Tax Withheld"
                                  value={formatIDR(pphHeld)}
                                  tone="text-sky-700"
                                />
                              )}
                              {retPct > 0 && (
                                <FinCell
                                  label={`Retention (${retPct}%)`}
                                  value={formatIDR(retentionHeld)}
                                  tone="text-amber-700"
                                />
                              )}
                              <FinCell
                                label="Net Received"
                                value={formatIDR(netReceived)}
                                tone="text-emerald-700"
                              />
                            </>
                          )}
                          {p.status === "completed" && (
                            <FinCell label="Closed By" value={<>{p.closed_by_name || "-"}<div className="text-[10px] text-slate-500">{p.closed_at ? fmtDate(p.closed_at) : ""}</div></>} />
                          )}
                        </div>
                        {canSeeAccess && (
                          <AccessListPanel projectId={p.id} isChild={isChild} canManage={canManageAccess} />
                        )}
                      </td>
                    </tr>
                  )}
                </FragmentRow>
              );
            })}
          </tbody>
        </table>
      </section>
      </>
      )}

      <ActiveProjectDialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setCreatePreset(null); }} onCreated={load}
        presetCategory={createPreset?.category} presetType={createPreset?.type} />
      <EditProjectDialog open={editDlg.open} onOpenChange={(o) => setEditDlg({ ...editDlg, open: o })} project={editDlg.project} onSaved={load} />
      {/* R227/R229: drill-down rincian profit & kelengkapan biaya (komponen sama
          dgn Monitoring & Cost Control) — read-only; pid dipertahankan saat
          tutup agar isi tidak berubah jadi empty state. */}
      <ProfitDrilldownDialog
        open={profitDrillOpen}
        onOpenChange={setProfitDrillOpen}
        view={profitDrillPid ? ccSummaries[profitDrillPid]?.profit_view || null : null}
        projectName={profitDrillPid ? projects.find((p) => p.id === profitDrillPid)?.project_name : null}
      />
      <CloseProjectDialog open={closeDlg.open} onOpenChange={(o) => setCloseDlg({ ...closeDlg, open: o })} project={closeDlg.project} onDone={load} />
      {lifecycleDlg.open && (
        <ProjectLifecycleDialog
          open={lifecycleDlg.open}
          project={lifecycleDlg.project}
          onClose={() => setLifecycleDlg({ open: false, project: null })}
          onChanged={load}
        />
      )}      <IncomeLedgerDialog open={incDlg.open} onOpenChange={(o) => setIncDlg({ ...incDlg, open: o })} project={incDlg.project} onChanged={load} readOnly={!canLedger} />
      <ExpenseLedgerDialog open={expDlg.open} onOpenChange={(o) => setExpDlg({ ...expDlg, open: o })} project={expDlg.project} onChanged={load} readOnly={!canLedger} />
      <RetentionClaimsDialog
        open={retentionDlg}
        onOpenChange={setRetentionDlg}
        onOpenLedger={(project) => setIncDlg({ open: true, project })}
      />
      <BulkChangeParentDialog
        open={bulkDlg.open}
        onOpenChange={(o) => setBulkDlg({ ...bulkDlg, open: o })}
        childProjects={bulkDlg.projects}
        onSaved={() => { bulkDlg.onClear?.(); load(); }}
      />
      <DeleteProjectDialog
        open={deleteDlg.open}
        onOpenChange={(o) => setDeleteDlg({ ...deleteDlg, open: o })}
        project={deleteDlg.project}
        isCompleted={deleteDlg.isCompleted}
        onConfirm={doDeleteProject}
      />
      {/* 149-cw-r116 (2026-08-29): Budget Alert Dialog — daftar project Over/Near budget. */}
      <BudgetAlertDialog
        open={budgetAlertDlg.open}
        onOpenChange={(o) => setBudgetAlertDlg({ ...budgetAlertDlg, open: o })}
        kind={budgetAlertDlg.kind}
        projects={projects}
        onProjectClick={(p) => {
          setBudgetAlertDlg({ open: false, kind: null });
          setTab("summary");
          setSearchQ(p.project_name || "");
        }}
      />
    </div>
  );
}

function FragmentRow({ children }) { return <>{children}</>; }

/**
 * Monitoring AR (Account Receivable) — flat list dari semua invoice / income entry
 * lintas project (kecuali internal). Kolom: Project · Client · Invoice Date ·
 * Received Date · Amount · Net Received · Status (Outstanding / Received / Overdue).
 */
/**
 * Monitoring Working Order — daftar semua WO project dengan kolom: WO Number,
 * PO Number, PO Value, SPH Submit Date (Latest), Site Survey Date, Project Done Date,
 * Retention %, Tanggal Jatuh Tempo Penagihan Retention.
 */
function FinCell({ label, value, tone = "text-[#0B1D3A]" }) {
  return (
    <div>
      <div className="overline text-slate-500">{label}</div>
      <div className={`mt-1 font-semibold tabular-nums ${tone}`}>{value}</div>
    </div>
  );
}


/**
 * WOTabContainer — 149-bp. Wrapper untuk tab Monitoring Working Order dengan
 * toggle MASTER-DETAIL (default, layout baru) ↔ BOARD (legacy WOMonitoringView).
 * Preferensi user disimpan di localStorage supaya persist antar session.
 */
function WOTabContainer({ projects, view, onOpenEdit, onBulkChangeParent, onChanged, onDelete }) {
  // 149-cw-r89: view state di-lift ke ProjectsPage supaya toggle bisa
  // di-render inline dgn tab bar (di-slot kanan). WOTabContainer sekarang
  // sekadar switch antara Master-Detail vs Board.
  return (
    <div className="space-y-3" data-testid="wo-tab-container">
      {view === "master_detail" ? (
        <WOMasterDetailView projects={projects} onOpenEdit={onOpenEdit} onChanged={onChanged} onDelete={onDelete} />
      ) : (
        <WOMonitoringView projects={projects} onOpenEdit={onOpenEdit} onBulkChangeParent={onBulkChangeParent} onDelete={onDelete} />
      )}
    </div>
  );
}

